import { Checkbox } from "@/components/ui/checkbox";
import type { ChannelRef, GrantScopes, Grantable, PersonRef, ScopeChoice, SkillInfo } from "@/lib/api";
import { KIND_COPY, type ScopeKind, channelKey, memoriesFrom, num, personKey, plainText, skillItem, skillParts } from "@/lib/grant-scopes";
import { cn } from "@/lib/utils";
import { BookOpen, Building2, Check, ChevronRight, Hash, KeyRound, UserRound, Wrench } from "lucide-react";
import { Many } from "@/components/grants/Many";
import { type ReactNode, useState } from "react";

// The scope checklist behind every consent screen: what a source has, by
// kind, with each item checkable. `bound` limits what can be chosen (what was
// asked for, or what was granted); kinds outside it are left out. Each kind
// starts folded to a header with its counts and how much of it is chosen.
// "All …" rows follow the source as it learns: new skills, channels and
// people are included as they appear.

const KIND_ICON: Record<ScopeKind, typeof Wrench> = {
  skills: Wrench,
  org: Building2,
  channels: Hash,
  people: UserRound,
};

// `sub` names an item whose source has no display name for it.
type Item = { key: string; label: string; sub?: string; count: number; meta: string; titles: string[]; origins?: string[] };

function inChoice<T>(c: ScopeChoice<T>, key: (t: T) => string, k: string) {
  return "all" in c || c.only.some((x) => key(x) === k);
}

export function ScopeEditor({
  grantable,
  value,
  onChange,
  bound,
  readOnly,
  showTitles = true,
  disabled,
  skillsAllOrNone,
  use,
}: {
  grantable: Grantable;
  value: GrantScopes;
  onChange?: (next: GrantScopes) => void;
  /** Only these can be chosen. Absent: anything the source has. */
  bound?: GrantScopes;
  readOnly?: boolean;
  showTitles?: boolean;
  disabled?: boolean;
  /** The source can share every skill set or none (older version): no picking within. */
  skillsAllOrNone?: boolean;
  /** The learning Boo picking what it uses, rather than a source choosing what it shares: "In use" wording. */
  use?: boolean;
}) {
  const set = (next: GrantScopes) => onChange?.(next);
  const allowed = {
    skills: !bound || "all" in bound.skills || bound.skills.only.length > 0,
    org: !bound || bound.memories.org,
    channels: !bound || "all" in bound.memories.channels || bound.memories.channels.only.length > 0,
    people: !bound || "all" in bound.memories.people || bound.memories.people.only.length > 0,
  };
  const canAll = {
    skills: !bound || "all" in bound.skills,
    channels: !bound || "all" in bound.memories.channels,
    people: !bound || "all" in bound.memories.people,
  };

  // Skill sets the bound reaches, each with the skills that can be chosen in
  // it and whether the whole set (with skills added later) can be.
  const boundItems = bound && !("all" in bound.skills) ? bound.skills.only : null;
  const sets = grantable.skills
    // A set with no skills yet has nothing to choose; "All skill sets" still covers it.
    .filter((r) => r.names.length > 0)
    .filter((r) => !boundItems || boundItems.some((i) => skillParts(i).registry === r.registry))
    .map((r) => {
      const whole = !boundItems || boundItems.includes(r.registry);
      const names = whole ? r.names : r.names.filter((n) => boundItems!.includes(skillItem(r.registry, n)));
      const info = new Map((r.items ?? []).map((i) => [i.name, i]));
      return { registry: r.registry, label: r.namespace || r.registry, count: whole ? r.count : names.length, names, whole, info };
    });
  type SkillSet = (typeof sets)[number];
  const skillBase = (): string[] => ("all" in value.skills ? sets.map((r) => r.registry) : value.skills.only);
  const pickedIn = (r: SkillSet): string[] =>
    "all" in value.skills || value.skills.only.includes(r.registry)
      ? r.names
      : r.names.filter((n) => (value.skills as { only: string[] }).only.includes(skillItem(r.registry, n)));
  const withSet = (r: SkillSet, names: string[]) => {
    const rest = skillBase().filter((i) => skillParts(i).registry !== r.registry);
    const usable = r.names.filter((n) => !r.info.get(n)?.invalid);
    const all = names.length > 0 && usable.every((n) => names.includes(n)) && r.whole;
    return { only: [...rest, ...(all ? [r.registry] : names.map((n) => skillItem(r.registry, n)))] };
  };

  const channels: Array<Item & { ref: ChannelRef }> = grantable.memories.channels
    .filter((c) => !bound || inChoice(bound.memories.channels, channelKey, channelKey(c)))
    .map((c) => ({
      key: channelKey(c),
      ref: { provider: c.provider, channelId: c.channelId },
      label: c.name ?? "Unnamed channel",
      ...(c.name ? {} : { sub: c.channelId }),
      count: c.count,
      meta: memoriesMeta(c.count, c.provider),
      titles: c.titles,
      origins: c.origins,
    }));
  const people: Array<Item & { ref: PersonRef }> = grantable.memories.people
    .filter((p) => !bound || inChoice(bound.memories.people, personKey, personKey(p)))
    .map((p) => ({
      key: personKey(p),
      ref: { provider: p.provider, providerUserId: p.providerUserId },
      label: p.name ?? "Someone without a name on file",
      ...(p.name ? {} : { sub: p.providerUserId }),
      count: p.count,
      meta: memoriesMeta(p.count, p.provider),
      origins: p.origins,
      // 1-on-1 titles stay out of the list: counts are enough to decide.
      titles: [],
    }));

  // Toggling one item out of "all" turns it into the list of everything else.
  function toggle<T>(choice: ScopeChoice<T>, items: Array<{ key: string; ref: T }>, key: (t: T) => string, k: string, on: boolean): ScopeChoice<T> {
    const current = "all" in choice ? items.map((i) => i.ref) : choice.only;
    const next = on ? [...current.filter((x) => key(x) !== k), items.find((i) => i.key === k)!.ref] : current.filter((x) => key(x) !== k);
    return { only: next };
  }

  const sections: ReactNode[] = [];
  // "All shared", "9 of 17 shared", "None shared"; "… in use" on the learning side.
  const word = use ? "in use" : "shared";
  const chosen = (all: boolean, picked: number, total: number) =>
    all || (total > 0 && picked === total) ? `All ${word}` : picked === 0 ? `None ${word}` : `${num(picked)} of ${num(total)} ${word}`;
  const later = (what: string) => `Also ${use ? "uses" : "shares"} ${what}`;

  if (allowed.skills) {
    // Counted over every skill set reached, including ones a source on an
    // older version lists without their skills.
    const reached = grantable.skills.filter((r) => !boundItems || boundItems.some((i) => skillParts(i).registry === r.registry));
    const singlesIn = (items: string[], r: { registry: string }) => items.filter((i) => i.includes("/") && skillParts(i).registry === r.registry).length;
    const sizeOf = (r: { registry: string; count: number }) => (!boundItems || boundItems.includes(r.registry) ? r.count : singlesIn(boundItems, r));
    const total = reached.reduce((n, r) => n + sizeOf(r), 0);
    const picked = reached.reduce(
      (n, r) => n + ("all" in value.skills || value.skills.only.includes(r.registry) ? sizeOf(r) : Math.min(singlesIn(value.skills.only, r), sizeOf(r))),
      0,
    );
    sections.push(
      <Section key="skills" kind="skills" detail={num(total)} chosen={chosen("all" in value.skills, picked, total)}>
        {canAll.skills && (
          <AllRow
            label={total ? `All ${num(total)} ${total === 1 ? "skill" : "skills"}` : "All skills"}
            hint={later("skills added later")}
            checked={"all" in value.skills}
            readOnly={readOnly}
            disabled={disabled}
            onChange={(on) => set({ ...value, skills: on ? { all: true } : { only: skillsAllOrNone ? [] : sets.map((r) => r.registry) } })}
          />
        )}
        <Many
          items={sets}
          noun="skills"
          find="Find a skill"
          showAll={(hidden) => `Show ${num(hidden.reduce((n, r) => n + r.count, 0))} more skills`}
          text={(r) => `${r.label} ${r.names.join(" ")} ${[...r.info.values()].map((i) => i.displayName).join(" ")}`}
          render={(r, query) => (
            <SkillSetRow
              key={r.registry}
              set={r}
              find={query}
              picked={pickedIn(r)}
              readOnly={readOnly || skillsAllOrNone}
              disabled={disabled}
              onChange={(names) => set({ ...value, skills: withSet(r, names) })}
            />
          )}
        />
        {sets.length === 0 && <Empty text={total > 0 ? "Skills can't be listed one by one on an older version." : "No skills yet."} />}
      </Section>,
    );
  }

  if (allowed.org) {
    const org = grantable.memories.org;
    sections.push(
      <Section key="org" kind="org" detail={num(org.count)} chosen={value.memories.org ? (use ? "In use" : "Shared") : use ? "Not in use" : "Not shared"}>
        <ItemRow
          item={{ key: "org", label: "Organization memories", count: org.count, meta: memoriesMeta(org.count), titles: org.titles, origins: org.origins }}
          showTitles={showTitles}
          checked={value.memories.org}
          readOnly={readOnly}
          disabled={disabled}
          onChange={(on) => set({ ...value, memories: { ...value.memories, org: on } })}
        />
      </Section>,
    );
  }

  if (allowed.channels) {
    sections.push(
      <Section
        key="channels"
        kind="channels"
        detail={memoriesFrom(channels.reduce((n, c) => n + c.count, 0), channels.length, "channel")}
        chosen={chosen("all" in value.memories.channels, channels.filter((c) => inChoice(value.memories.channels, channelKey, c.key)).length, channels.length)}
      >
        {canAll.channels && (
          <AllRow
            label={`All ${num(channels.length)} ${channels.length === 1 ? "channel" : "channels"}`}
            hint={later("channels added later")}
            checked={"all" in value.memories.channels}
            readOnly={readOnly}
            disabled={disabled}
            onChange={(on) =>
              set({ ...value, memories: { ...value.memories, channels: on ? { all: true } : { only: channels.map((c) => c.ref) } } })
            }
          />
        )}
        <Many
          items={channels}
          noun="channels"
          text={(c) => `${c.label} ${c.sub ?? ""}`}
          render={(c) => (
            <ItemRow
              key={c.key}
              item={c}
              showTitles={showTitles}
              checked={inChoice(value.memories.channels, channelKey, c.key)}
              readOnly={readOnly}
              disabled={disabled}
              onChange={(on) =>
                set({ ...value, memories: { ...value.memories, channels: toggle(value.memories.channels, channels, channelKey, c.key, on) } })
              }
            />
          )}
        />
        {channels.length === 0 && <Empty text="Nothing learned in channels yet." />}
      </Section>,
    );
  }

  if (allowed.people) {
    sections.push(
      <Section
        key="people"
        kind="people"
        detail={memoriesFrom(people.reduce((n, p) => n + p.count, 0), people.length, "person", "people")}
        chosen={chosen("all" in value.memories.people, people.filter((p) => inChoice(value.memories.people, personKey, p.key)).length, people.length)}
      >
        {canAll.people && (
          <AllRow
            label={`All ${num(people.length)} ${people.length === 1 ? "person" : "people"}`}
            hint={later("future one-on-one conversations")}
            checked={"all" in value.memories.people}
            readOnly={readOnly}
            disabled={disabled}
            onChange={(on) =>
              set({ ...value, memories: { ...value.memories, people: on ? { all: true } : { only: people.map((p) => p.ref) } } })
            }
          />
        )}
        <Many
          items={people}
          noun="people"
          text={(p) => `${p.label} ${p.sub ?? ""}`}
          render={(p) => (
            <ItemRow
              key={p.key}
              item={p.sub ? p : { ...p, label: `With ${p.label}` }}
              showTitles={false}
              checked={inChoice(value.memories.people, personKey, p.key)}
              readOnly={readOnly}
              disabled={disabled}
              onChange={(on) =>
                set({ ...value, memories: { ...value.memories, people: toggle(value.memories.people, people, personKey, p.key, on) } })
              }
            />
          )}
        />
        {people.length === 0 && <Empty text="No one-on-one conversations yet." />}
      </Section>,
    );
  }

  if (sections.length === 0) return <p className="text-sm text-muted-foreground">Nothing to choose from.</p>;
  return <div className="flex flex-col gap-3">{sections}</div>;
}

const PROVIDER: Record<string, string> = { slack: "Slack", teams: "Teams", gchat: "Google Chat", discord: "Discord", dashboard_chat: "Dashboard" };

function memoriesMeta(count: number, provider?: string) {
  const n = `${num(count)} ${count === 1 ? "memory" : "memories"}`;
  return provider && PROVIDER[provider] ? `${n} · ${PROVIDER[provider]}` : n;
}

/** One kind, folded to "Channel memories  125 from 17 channels" until opened. */
function Section({ kind, detail, chosen, children }: { kind: ScopeKind; detail: string; chosen: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const Icon = KIND_ICON[kind];
  return (
    <section className="rounded-lg border border-border">
      <h3>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          title={KIND_COPY[kind].hint}
          className={cn(
            "flex w-full items-start gap-3 rounded-lg bg-muted/40 px-4 py-2.5 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            open && "rounded-b-none border-b border-border",
          )}
        >
          <ChevronRight className={cn("mt-0.5 size-4 shrink-0 text-muted-foreground motion-safe:transition-transform", open && "rotate-90")} aria-hidden />
          <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-medium">{KIND_COPY[kind].label}</span>
              <span className="text-sm tabular-nums text-muted-foreground">{detail}</span>
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">{chosen}</span>
          </span>
        </button>
      </h3>
      {open && <div className="flex flex-col divide-y divide-border">{children}</div>}
    </section>
  );
}

function Mark({ checked, readOnly, disabled, onChange, label }: { checked: boolean; readOnly?: boolean; disabled?: boolean; onChange: (on: boolean) => void; label: string }) {
  if (readOnly) {
    return (
      <span className={cn("flex size-4 shrink-0 items-center justify-center", checked ? "text-foreground" : "text-muted-foreground/40")} aria-label={checked ? `${label}: included` : `${label}: not included`}>
        {checked ? <Check className="size-4" /> : <span className="size-1.5 rounded-full bg-current" />}
      </span>
    );
  }
  return <Checkbox checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(v === true)} aria-label={label} />;
}

function AllRow({ label, hint, checked, readOnly, disabled, onChange }: { label: string; hint: string; checked: boolean; readOnly?: boolean; disabled?: boolean; onChange: (on: boolean) => void }) {
  if (readOnly && !checked) return null;
  return (
    <label className={cn("flex items-start gap-3 px-4 py-2.5 text-sm", !readOnly && !disabled && "cursor-pointer hover:bg-muted/30")}>
      <span className="mt-0.5">
        <Mark checked={checked} readOnly={readOnly} disabled={disabled} onChange={onChange} label={label} />
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );
}

function ItemRow({ item, checked, readOnly, disabled, showTitles, onChange }: { item: Item; checked: boolean; readOnly?: boolean; disabled?: boolean; showTitles: boolean; onChange: (on: boolean) => void }) {
  return (
    <label className={cn("flex items-start gap-3 px-4 py-2.5 text-sm", !readOnly && !disabled && "cursor-pointer hover:bg-muted/30", readOnly && !checked && "opacity-60")}>
      <span className="mt-0.5">
        <Mark checked={checked} readOnly={readOnly} disabled={disabled} onChange={onChange} label={item.label} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {item.label}
            {item.sub && <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{item.sub}</span>}
            {item.origins && item.origins.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">· from {item.origins.join(", ")}</span>}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{item.meta}</span>
        </span>
        {showTitles && item.titles.length > 0 && (
          <span className="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground">
            <BookOpen className="mt-0.5 size-3 shrink-0" aria-hidden />
            <span className="min-w-0 [overflow-wrap:anywhere]">{item.titles.slice(0, 3).join(" · ")}</span>
          </span>
        )}
      </span>
    </label>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="px-4 py-2.5 text-xs text-muted-foreground">{text}</p>;
}

/**
 * One skill set: checked when all of it is on, partly checked when only some
 * skills are; opens to pick skill by skill, with what each skill does and
 * which of the source's saved secrets it can read.
 */
function SkillSetRow({
  set,
  picked,
  readOnly,
  disabled,
  find,
  onChange,
}: {
  set: { registry: string; label: string; count: number; names: string[]; whole: boolean; info: Map<string, SkillInfo> };
  picked: string[];
  /** The skill search: when it names skills in this set (not the set itself), it opens on just those. */
  find?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange: (names: string[]) => void;
}) {
  // Unset: open while a search narrows the set to some of its skills.
  const [open, setOpen] = useState<boolean | null>(null);
  // Skills that can't run aren't offered.
  const usable = set.names.filter((n) => !set.info.get(n)?.invalid);
  const on = picked.filter((n) => usable.includes(n));
  const some = on.length > 0 && on.length < usable.length;
  const state = on.length === 0 ? false : some ? "indeterminate" : true;
  const meta = some ? `${on.length} of ${usable.length} skills` : `${set.count} ${set.count === 1 ? "skill" : "skills"}`;
  const withSecrets = (readOnly || some ? on : usable).filter((n) => (set.info.get(n)?.secrets.length ?? 0) > 0).length;
  const label = (n: string) => set.info.get(n)?.displayName || n;
  const canOpen = set.names.length > 0;
  const q = find?.trim().toLowerCase() ?? "";
  const narrowed = !!q && !set.label.toLowerCase().includes(q);
  const shownNames = narrowed ? set.names.filter((n) => `${label(n)} ${n}`.toLowerCase().includes(q)) : set.names;
  const isOpen = open ?? (narrowed && shownNames.length > 0);
  return (
    <div className={cn(readOnly && on.length === 0 && "opacity-60")}>
      <div className="flex items-start gap-3 px-4 py-2.5 text-sm">
        <span className="mt-0.5">
          {readOnly ? (
            <ReadMark on={on.length > 0} />
          ) : (
            <Checkbox
              checked={state}
              disabled={disabled || usable.length === 0}
              aria-label={set.label}
              onCheckedChange={() => onChange(state === true ? [] : usable)}
            />
          )}
        </span>
        <button
          type="button"
          className="flex min-w-0 flex-1 flex-col items-stretch text-left disabled:cursor-default"
          onClick={() => setOpen(!isOpen)}
          disabled={!canOpen}
          aria-expanded={isOpen}
        >
          <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
            <span className="flex min-w-0 items-center gap-1">
              {canOpen && <ChevronRight className={cn("size-3.5 shrink-0 text-muted-foreground motion-safe:transition-transform", isOpen && "rotate-90")} aria-hidden />}
              <span className="[overflow-wrap:anywhere]">{set.label || set.registry}</span>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-xs tabular-nums text-muted-foreground">
              {withSecrets > 0 && (
                <span className="inline-flex items-center gap-1" title="Skills that run with the other Boo's saved secrets">
                  <KeyRound className="size-3" aria-hidden />
                  {withSecrets} with secrets
                </span>
              )}
              {meta}
            </span>
          </span>
          {!isOpen && canOpen && (
            <span className="mt-0.5 pl-[18px] text-xs text-muted-foreground [overflow-wrap:anywhere]">
              {(some || readOnly ? on : usable).slice(0, 4).map(label).join(" · ")}
              {(some || readOnly ? on : usable).length > 4 ? ` and ${num((some || readOnly ? on : usable).length - 4)} more` : ""}
            </span>
          )}
        </button>
      </div>
      {isOpen && (
        <div className="flex flex-col pb-2">
          <Many
            items={shownNames}
            noun="skills"
            indent
            text={(n) => `${label(n)} ${n} ${set.info.get(n)?.description ?? ""}`}
            render={(name) => {
              const info = set.info.get(name);
              const checked = on.includes(name);
              const broken = !!info?.invalid;
              const about = info?.description ? plainText(info.description) : "";
              return (
                <label
                  key={name}
                  className={cn(
                    "mx-4 ml-[44px] flex items-start gap-2.5 rounded-md px-2 py-1.5 text-sm",
                    !readOnly && !disabled && !broken && "cursor-pointer hover:bg-muted/40",
                    ((readOnly && !checked) || broken) && "opacity-50",
                  )}
                >
                  <span className="mt-0.5">
                    {readOnly ? (
                      <ReadMark on={checked} />
                    ) : (
                      <Checkbox
                        checked={checked}
                        disabled={disabled || broken}
                        onCheckedChange={(v) => onChange(v === true ? [...on, name] : on.filter((n) => n !== name))}
                      />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium [overflow-wrap:anywhere]">{label(name)}</span>
                    {info && info.displayName !== name && (
                      <span className="block font-mono text-[11px] text-muted-foreground [overflow-wrap:anywhere]">{name}</span>
                    )}
                    {broken ? (
                      <span className="block text-xs text-muted-foreground">Can't be shared: this skill's setup has errors, so it doesn't run anywhere.</span>
                    ) : about ? (
                      <span className="block text-xs text-muted-foreground [overflow-wrap:anywhere]">{about}</span>
                    ) : null}
                    {!broken && info && info.secrets.length > 0 && <SecretTags names={info.secrets} />}
                  </span>
                </label>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}

function ReadMark({ on }: { on: boolean }) {
  return (
    <span className={cn("flex size-4 shrink-0 items-center justify-center", on ? "text-foreground" : "text-muted-foreground/40")}>
      {on ? <Check className="size-4" /> : <span className="size-1.5 rounded-full bg-current" />}
    </span>
  );
}

const SECRETS_SHOWN = 4;

/** The saved secrets a skill reads, by name; past four, a count with the full list on hover. */
function SecretTags({ names }: { names: string[] }) {
  const rest = names.length - SECRETS_SHOWN;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1">
      <KeyRound className="size-3 text-muted-foreground" aria-label="Uses saved secrets" />
      {names.slice(0, SECRETS_SHOWN).map((secret) => (
        <span key={secret} className="max-w-full rounded bg-muted px-1.5 py-px font-mono text-[11px] text-muted-foreground [overflow-wrap:anywhere]">
          {secret}
        </span>
      ))}
      {rest > 0 && (
        <span className="text-[11px] text-muted-foreground" title={names.slice(SECRETS_SHOWN).join(", ")}>
          +{rest} more
        </span>
      )}
    </span>
  );
}
